import React, { useState, useEffect, useRef } from 'react';
import { 
  ArrowRight, ChevronRight, ChevronDown, Zap, Sparkles, Shield, Lock, Globe, 
  BarChart2, Users, Heart, Clock, Calendar, Sun, Moon, MessageCircle, 
  Coffee, Camera, Gift, Diamond, Crown, Palette, Music, BookOpen, Dumbbell,
  Award, Activity, Briefcase, Watch, Plane, Home, Smartphone, Settings
} from 'lucide-react';
import { motion, useAnimation, useInView } from 'framer-motion';

const Landing = ({  }) => {
  const [darkMode, setDarkMode] = useState(true);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [activeFeature, setActiveFeature] = useState(0);
  const controls = useAnimation();
  const ref = useRef();
  const isInView = useInView(ref, { once: true });

  // Luxury color themes
  const theme = {
    dark: {
      bg: 'bg-gradient-to-br from-slate-900 via-blue-900 to-emerald-900',
      text: 'text-blue-100',
      accent: 'from-blue-600 to-emerald-600',
      card: 'bg-slate-800/50 border-blue-500/20',
      button: 'bg-gradient-to-r from-blue-600 to-emerald-600 hover:from-blue-700 hover:to-emerald-700',
      primary: 'text-blue-100',
      secondary: 'text-blue-200/70'
    },
    light: {
      bg: 'bg-gradient-to-br from-blue-50 via-emerald-50 to-white',
      text: 'text-slate-900',
      accent: 'from-blue-500 to-emerald-500',
      card: 'bg-white/70 border-blue-600/20',
      button: 'bg-gradient-to-r from-blue-500 to-emerald-500 hover:from-blue-600 hover:to-emerald-600',
      primary: 'text-slate-900',
      secondary: 'text-slate-700'
    }
  };

  const currentTheme = darkMode ? theme.dark : theme.light;

  // Floating cards data
  const floatingCards = [
    {
      icon: <Users className="w-6 h-6" />,
      title: "Social Connections",
      value: "95% compatibility",
      bg: darkMode ? 'bg-blue-900/30' : 'bg-blue-100/70',
      color: darkMode ? 'text-blue-400' : 'text-blue-600',
      position: "bottom-left"
    },
    {
      icon: <Award className="w-6 h-6" />,
      title: "Daily Goals",
      value: "78% completed",
      bg: darkMode ? 'bg-emerald-900/30' : 'bg-emerald-100/70',
      color: darkMode ? 'text-emerald-400' : 'text-emerald-600',
      position: "top-right"
    },
    {
      icon: <Activity className="w-6 h-6" />,
      title: "Wellness Score",
      value: "89/100",
      bg: darkMode ? 'bg-purple-900/30' : 'bg-purple-100/70',
      color: darkMode ? 'text-purple-400' : 'text-purple-600',
      position: "top-left"
    },
    {
      icon: <Briefcase className="w-6 h-6" />,
      title: "Productivity",
      value: "4.8/5.0",
      bg: darkMode ? 'bg-amber-900/30' : 'bg-amber-100/70',
      color: darkMode ? 'text-amber-400' : 'text-amber-600',
      position: "bottom-right"
    }
  ];

  // Animated features
  const features = [
    {
      icon: <BarChart2 className="w-8 h-8" />,
      title: "Advanced Analytics",
      description: "Real-time insights with predictive intelligence for your lifestyle optimization"
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: "Privacy First",
      description: "Military-grade encryption protecting your exclusive data"
    },
    {
      icon: <Globe className="w-8 h-8" />,
      title: "Global Access",
      description: "Seamless experience across all your devices worldwide"
    },
    {
      icon: <Zap className="w-8 h-8" />,
      title: "Instant Actions",
      description: "One-tap access to your most used premium services"
    }
  ];

  // Luxury lifestyle images
  const lifestyleImages = [
    "https://images.unsplash.com/photo-1605000797499-95a51c5269ae?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1471&q=80",
    "https://images.unsplash.com/photo-1556388158-158ea5ccacbd?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80",
    "https://images.unsplash.com/photo-1523170335258-f5ed11844a49?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1480&q=80",
    "https://images.unsplash.com/photo-1544161515-4ab6ce6db874?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80"
  ];

  // Animated testimonials
  const testimonials = [
    {
      quote: "This platform transformed how I manage my time and luxury experiences. Absolute game-changer.",
      author: "Sophia K.",
      role: "Fortune 500 Executive",
      image: "https://randomuser.me/api/portraits/women/65.jpg"
    },
    {
      quote: "The attention to detail and personalized recommendations are unmatched in the market.",
      author: "James L.",
      role: "Private Investor",
      image: "https://randomuser.me/api/portraits/men/32.jpg"
    },
    {
      quote: "Finally a service that understands the needs of discerning individuals like myself.",
      author: "Elena V.",
      role: "Art Collector",
      image: "https://randomuser.me/api/portraits/women/44.jpg"
    }
  ];

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (isInView) {
      controls.start("visible");
    }
  }, [isInView, controls]);

  // Auto-rotate features
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveFeature((prev) => (prev + 1) % features.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  // Floating card variants
  const floatingCardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.2,
        duration: 0.8,
        ease: "easeOut"
      }
    })
  };

  const onGetStarted = () => {
    window.location = '/AbiAuth';
  }

  // Image parallax effect
  const handleMouseMove = (e) => {
    const { left, top, width, height } = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - left) / width;
    const y = (e.clientY - top) / height;
    e.currentTarget.style.transform = `perspective(1000px) rotateX(${(y - 0.5) * 10}deg) rotateY(${(x - 0.5) * -10}deg)`;
  };

  const handleMouseLeave = (e) => {
    e.currentTarget.style.transform = 'perspective(1000px) rotateX(0) rotateY(0)';
  };

  return (
    <div className={`min-h-screen transition-colors duration-1000 ${currentTheme.bg} ${currentTheme.text}`}>
      {/* Animated floating elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        {[...Array(30)].map((_, i) => (
          <motion.div
            key={i}
            className={`absolute rounded-full ${darkMode ? 'bg-blue-400' : 'bg-emerald-600'} opacity-10`}
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              width: `${Math.random() * 10 + 5}px`,
              height: `${Math.random() * 10 + 5}px`,
            }}
            animate={{
              y: [0, -20, 0],
              x: [0, Math.random() * 40 - 20, 0],
              rotate: [0, Math.random() * 360],
            }}
            transition={{
              duration: Math.random() * 10 + 10,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
        ))}
      </div>

      {/* Main content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 py-20">
        {/* Header */}
        <header className="flex justify-between items-center mb-24">
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="flex items-center space-x-3"
          >
            <div className={`w-10 h-10 rounded-full bg-gradient-to-r ${currentTheme.accent} flex items-center justify-center`}>
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-light">ABILOOP</span>
          </motion.div>
          
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="flex items-center space-x-6"
          >
            <div className={`hidden md:flex items-center space-x-2 ${currentTheme.text}`}>
              <Clock className="w-5 h-5" />
              <span>{currentTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
            </div>
            <button
              onClick={() => setDarkMode(!darkMode)}
              className={`p-2 rounded-full ${darkMode ? 'bg-blue-900/30' : 'bg-blue-100'} transition-colors duration-300`}
            >
              {darkMode ? (
                <Sun className="w-5 h-5 text-blue-400" />
              ) : (
                <Moon className="w-5 h-5 text-blue-600" />
              )}
            </button>
          </motion.div>
        </header>

        {/* Hero section */}
        <section className="mb-32">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div 
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              className="space-y-8"
            >
              <h1 className="text-5xl md:text-6xl font-light leading-tight">
                Elevate Your <span className="font-medium">Luxury</span> Lifestyle
              </h1>
              <p className={`text-xl ${darkMode ? 'text-blue-200/80' : 'text-slate-700'} max-w-lg`}>
                The ultimate platform for discerning individuals who demand excellence in every aspect of their digital experience.
              </p>
              <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-6 pt-6">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={onGetStarted}
                  className={`${currentTheme.button} text-white px-8 py-4 rounded-full font-medium flex items-center space-x-2 transition-all hover:shadow-lg`}
                >
                  <span>Get Started</span>
                  <ArrowRight className="w-5 h-5" />
                </motion.button>
                <motion.button 
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className={`px-8 py-4 rounded-full font-medium flex items-center space-x-2 border ${
                    darkMode ? 'border-blue-400/30 hover:bg-blue-900/20' : 'border-blue-600/30 hover:bg-blue-100/50'
                  } transition-all`}
                >
                  <span>Explore Features</span>
                  <ChevronDown className="w-5 h-5" />
                </motion.button>
              </div>
            </motion.div>
            
            <motion.div 
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className={`absolute -top-16 -left-16 w-64 h-64 rounded-full bg-gradient-to-r ${currentTheme.accent} opacity-20 blur-3xl`} />
              <div className={`absolute -bottom-16 -right-16 w-64 h-64 rounded-full bg-gradient-to-r ${currentTheme.accent} opacity-20 blur-3xl`} />
              <motion.div 
                onMouseMove={handleMouseMove}
                onMouseLeave={handleMouseLeave}
                className={`relative rounded-3xl overflow-hidden border ${
                  darkMode ? 'border-blue-500/30' : 'border-blue-600/30'
                } shadow-2xl transition-transform duration-500 ease-out`}
                style={{ transformStyle: 'preserve-3d' }}
              >
                <motion.img 
                  src={lifestyleImages[0]}
                  alt="Luxury interface preview"
                  className="w-full h-full object-cover"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.8 }}
                />
                <div className={`absolute inset-0 bg-gradient-to-t ${
                  darkMode ? 'from-slate-900/80 via-slate-900/20' : 'from-white/80 via-white/20'
                }`} />
              </motion.div>
              
              {/* Floating cards */}
              {floatingCards.map((card, i) => (
                <motion.div
                  key={i}
                  custom={i}
                  initial="hidden"
                  animate="visible"
                  variants={floatingCardVariants}
                  className={`absolute p-6 rounded-2xl backdrop-blur-md ${
                    darkMode ? 'bg-slate-800/50 border border-slate-700' : 'bg-white/80 border border-blue-200'
                  } shadow-lg ${
                    card.position === 'bottom-left' ? '-bottom-6 -left-6' :
                    card.position === 'top-right' ? '-top-6 -right-6' :
                    card.position === 'top-left' ? '-top-6 -left-6' : '-bottom-6 -right-6'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <div className={`p-3 rounded-lg ${card.bg}`}>
                      {React.cloneElement(card.icon, { className: `w-6 h-6 ${card.color}` })}
                    </div>
                    <div>
                      <p className={`text-sm font-medium ${currentTheme.primary}`}>{card.title}</p>
                      <p className={`text-xs ${currentTheme.secondary}`}>{card.value}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* Features section */}
        <section className="mb-32" ref={ref}>
          <motion.div 
            initial={{ opacity: 0, y: 50 }}
            animate={controls}
            variants={{
              hidden: { opacity: 0, y: 50 },
              visible: { opacity: 1, y: 0, transition: { duration: 0.8 } }
            }}
            className="flex justify-between items-end mb-16"
          >
            <div>
              <h2 className="text-3xl font-light mb-4">Exclusive Features</h2>
              <p className={`max-w-lg ${darkMode ? 'text-blue-200/70' : 'text-slate-600'}`}>
                Designed for those who appreciate the finer things in life, with unparalleled attention to detail.
              </p>
            </div>
            <button className={`hidden md:flex items-center space-x-2 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>
              <span>View all features</span>
              <ChevronRight className="w-5 h-5" />
            </button>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={controls}
                variants={{
                  hidden: { opacity: 0, y: 30 },
                  visible: { 
                    opacity: 1, 
                    y: 0, 
                    transition: { 
                      delay: index * 0.1,
                      duration: 0.6 
                    } 
                  }
                }}
                whileHover={{ y: -10 }}
                className={`p-8 rounded-3xl backdrop-blur-md ${currentTheme.card} border transition-all hover:shadow-xl cursor-pointer ${
                  activeFeature === index ? 'ring-2 ring-blue-400/50' : ''
                }`}
                onClick={() => setActiveFeature(index)}
              >
                <div className={`w-14 h-14 rounded-2xl mb-6 flex items-center justify-center transition-colors ${
                  darkMode ? 'bg-blue-900/30 text-blue-400' : 'bg-blue-100/70 text-blue-600'
                } ${activeFeature === index ? (darkMode ? 'bg-blue-900/50' : 'bg-blue-100') : ''}`}>
                  {feature.icon}
                </div>
                <h3 className="text-xl font-medium mb-3">{feature.title}</h3>
                <p className={`${darkMode ? 'text-blue-200/70' : 'text-slate-600'}`}>{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Animated showcase */}
        <section className="mb-32">
          <motion.div 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true, margin: "-100px" }}
            className={`rounded-4xl overflow-hidden ${currentTheme.card} border`}
          >
            <div className="grid grid-cols-1 lg:grid-cols-2">
              <div className="p-12 lg:p-16">
                <div className="flex items-center space-x-3 mb-6">
                  <Diamond className={`w-6 h-6 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`} />
                  <span className="font-medium">PREMIUM EXPERIENCE</span>
                </div>
                <h2 className="text-3xl font-light mb-6">Tailored Excellence</h2>
                <p className={`text-lg mb-8 ${darkMode ? 'text-blue-200/80' : 'text-slate-700'}`}>
                  Our concierge service learns your preferences to deliver perfectly curated experiences, from private jet charters to exclusive dining.
                </p>
                <div className="space-y-6">
                  {[
                    "24/7 Personal Concierge",
                    "Exclusive Event Access",
                    "Global Luxury Network",
                    "Discreet & Secure"
                  ].map((item, index) => (
                    <motion.div 
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1, duration: 0.5 }}
                      viewport={{ once: true }}
                      className="flex items-center space-x-4"
                    >
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        darkMode ? 'bg-blue-900/30 text-blue-400' : 'bg-blue-100/70 text-blue-600'
                      }`}>
                        <Check className="w-4 h-4" />
                      </div>
                      <span>{item}</span>
                    </motion.div>
                  ))}
                </div>
              </div>
              <div className="relative min-h-96 lg:min-h-full">
                <motion.div
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  transition={{ duration: 1 }}
                  viewport={{ once: true }}
                  className="absolute inset-0 w-full h-full"
                >
                  <motion.img 
                    src={lifestyleImages[1]}
                    alt="Premium luxury experience"
                    className="absolute inset-0 w-full h-full object-cover"
                    initial={{ scale: 1 }}
                    whileHover={{ scale: 1.05 }}
                    transition={{ duration: 0.5 }}
                  />
                  <div className={`absolute inset-0 bg-gradient-to-t ${
                    darkMode ? 'from-slate-900/70 via-slate-900/30' : 'from-white/70 via-white/30'
                  } lg:bg-gradient-to-l lg:from-slate-900/0 lg:via-slate-900/0 lg:to-slate-900/70`} />
                </motion.div>
              </div>
            </div>
          </motion.div>
        </section>

        {/* Animated testimonials */}
        <section className="mb-32">
          <motion.h2 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-3xl font-light mb-16 text-center"
          >
            Trusted by Elite Clientele
          </motion.h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1, duration: 0.6 }}
                viewport={{ once: true }}
                whileHover={{ y: -10 }}
                className={`p-8 rounded-3xl backdrop-blur-md ${currentTheme.card} border`}
              >
                <div className="flex items-center mb-6 space-x-3">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className={`w-5 h-5 ${darkMode ? 'text-yellow-400' : 'text-yellow-500'}`} fill="currentColor" />
                  ))}
                </div>
                <p className={`text-lg italic mb-8 ${darkMode ? 'text-blue-200/80' : 'text-slate-700'}`}>
                  "{testimonial.quote}"
                </p>
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-blue-400">
                    <img 
                      src={testimonial.image} 
                      alt={testimonial.author}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div>
                    <p className="font-medium">{testimonial.author}</p>
                    <p className={`text-sm ${darkMode ? 'text-blue-200/60' : 'text-slate-500'}`}>{testimonial.role}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Interactive CTA section */}
        <motion.section 
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className={`rounded-4xl p-12 lg:p-16 text-center ${currentTheme.card} border relative overflow-hidden`}
        >
          <div className="absolute inset-0 overflow-hidden">
            {[...Array(8)].map((_, i) => (
              <motion.div
                key={i}
                className={`absolute rounded-full ${darkMode ? 'bg-blue-400/10' : 'bg-emerald-600/10'}`}
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  width: `${Math.random() * 300 + 100}px`,
                  height: `${Math.random() * 300 + 100}px`,
                }}
                animate={{
                  x: [0, Math.random() * 200 - 100],
                  y: [0, Math.random() * 200 - 100],
                  rotate: [0, 360],
                }}
                transition={{
                  duration: Math.random() * 20 + 20,
                  repeat: Infinity,
                  repeatType: "reverse",
                  ease: "linear",
                }}
              />
            ))}
          </div>
          <div className="relative z-10 max-w-3xl mx-auto">
            <motion.div 
              animate={{
                rotate: [0, 5, -5, 0],
                scale: [1, 1.05, 1]
              }}
              transition={{
                duration: 8,
                repeat: Infinity,
                repeatType: "reverse",
                ease: "easeInOut"
              }}
              className={`w-20 h-20 mx-auto rounded-2xl mb-8 flex items-center justify-center ${
                darkMode ? 'bg-blue-900/30 text-blue-400' : 'bg-blue-100/70 text-blue-600'
              }`}
            >
              <Crown className="w-8 h-8" />
            </motion.div>
            <h2 className="text-3xl font-light mb-6">Ready to Experience the Difference?</h2>
            <p className={`text-xl mb-8 ${darkMode ? 'text-blue-200/80' : 'text-slate-700'}`}>
              Join our exclusive community of discerning individuals who demand nothing but the best.
            </p>
            <motion.button
              whileHover={{ 
                scale: 1.05,
                boxShadow: darkMode ? "0 10px 25px -5px rgba(59, 130, 246, 0.4)" : "0 10px 25px -5px rgba(16, 185, 129, 0.4)"
              }}
              whileTap={{ scale: 0.95 }}
              onClick={onGetStarted}
              className={`${currentTheme.button} text-white px-8 py-4 rounded-full font-medium flex items-center space-x-2 mx-auto transition-all`}
            >
              <span>Begin Your Journey</span>
              <ArrowRight className="w-5 h-5" />
            </motion.button>
          </div>
        </motion.section>

        {/* Footer */}
        <motion.footer 
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="mt-32 pt-16 border-t border-blue-900/20"
        >
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
            <div>
              <div className="flex items-center space-x-3 mb-6">
                <div className={`w-10 h-10 rounded-full bg-gradient-to-r ${currentTheme.accent} flex items-center justify-center`}>
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
                <span className="text-xl font-light">ABILOOP</span>
              </div>
              <p className={`${darkMode ? 'text-blue-200/70' : 'text-slate-600'} mb-6`}>
                The ultimate platform for luxury lifestyle management.
              </p>
              <div className="flex space-x-4">
                {[Twitter, Instagram, LinkedIn].map((Icon, index) => (
                  <motion.a 
                    key={index} 
                    href="#" 
                    className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      darkMode ? 'bg-blue-900/30 hover:bg-blue-900/50' : 'bg-blue-100/70 hover:bg-blue-100'
                    } transition-colors`}
                    whileHover={{ y: -5 }}
                  >
                    <Icon className={`w-5 h-5 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`} />
                  </motion.a>
                ))}
              </div>
            </div>
            
            {[
              {
                title: "Product",
                links: ["Features", "Pricing", "Premium", "Enterprise"]
              },
              {
                title: "Resources",
                links: ["Blog", "Guides", "Help Center", "API"]
              },
              {
                title: "Company",
                links: ["About", "Careers", "Privacy", "Terms"]
              }
            ].map((column, index) => (
              <div key={index}>
                <h4 className="font-medium mb-6">{column.title}</h4>
                <ul className="space-y-4">
                  {column.links.map((link, i) => (
                    <motion.li 
                      key={i}
                      whileHover={{ x: 5 }}
                      transition={{ type: "spring", stiffness: 300 }}
                    >
                      <a 
                        href="#" 
                        className={`hover:text-blue-400 transition-colors ${
                          darkMode ? 'text-blue-200/70' : 'text-slate-600'
                        }`}
                      >
                        {link}
                      </a>
                    </motion.li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          
          <div className={`pt-8 border-t border-blue-900/10 flex flex-col md:flex-row justify-between items-center ${
            darkMode ? 'text-blue-200/60' : 'text-slate-500'
          } text-sm`}>
            <p>© 2023 ABILOOP. All rights reserved.</p>
            <div className="flex space-x-6 mt-4 md:mt-0">
              <a href="#" className="hover:text-blue-400 transition-colors">Privacy Policy</a>
              <a href="#" className="hover:text-blue-400 transition-colors">Terms of Service</a>
              <a href="#" className="hover:text-blue-400 transition-colors">Cookies</a>
            </div>
          </div>
        </motion.footer>
      </div>

      <style jsx>{`
        @keyframes float {
          0%, 100% {
            transform: translateY(0) rotate(0deg);
          }
          50% {
            transform: translateY(-20px) rotate(5deg);
          }
        }
      `}</style>
    </div>
  );
};

// Social icon components
const Twitter = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723 10.054 10.054 0 01-3.127 1.184 4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z" />
  </svg>
);

const Instagram = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
  </svg>
);

const LinkedIn = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
  </svg>
);

const Check = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M20 6L9 17l-5-5" />
  </svg>
);

const Star = ({ className, fill = 'none' }) => (
  <svg className={className} viewBox="0 0 24 24" fill={fill} stroke="currentColor" strokeWidth="1.5">
    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
  </svg>
);

export default Landing;